import { useEffect, useState, useSyncExternalStore } from "react";

/** The current time, updated every 30 seconds and when the app comes back into view. */
export function useNow(intervalMs = 30_000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const tick = () => setNow(new Date());
    const timer = setInterval(tick, intervalMs);
    const onVisible = () => document.visibilityState === "visible" && tick();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [intervalMs]);
  return now;
}

function subscribePath(listener: () => void) {
  window.addEventListener("popstate", listener);
  window.addEventListener("leven:navigate", listener);
  return () => {
    window.removeEventListener("popstate", listener);
    window.removeEventListener("leven:navigate", listener);
  };
}

export function usePath(): string {
  return useSyncExternalStore(subscribePath, () => window.location.pathname);
}

export function navigate(path: string, { replace = false } = {}): void {
  if (window.location.pathname + window.location.search === path) return;
  if (replace) window.history.replaceState(null, "", path);
  else window.history.pushState(null, "", path);
  window.dispatchEvent(new Event("leven:navigate"));
}

/**
 * Folded in or out, remembered on this device (in and out folding of groups and blocks, B32). Browser storage may be
 * missing (private window): then it simply starts open.
 */
export function useCollapsed(key: string): [boolean, () => void] {
  const storageKey = `collapsed:${key}`;
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem(storageKey) === "1";
    } catch {
      return false;
    }
  });
  const toggle = () =>
    setCollapsed((was) => {
      const next = !was;
      try {
        if (next) window.localStorage.setItem(storageKey, "1");
        else window.localStorage.removeItem(storageKey);
      } catch {
        // Not kept: fine for this visit.
      }
      return next;
    });
  return [collapsed, toggle];
}
